import React, { useState, useEffect } from 'react';
import ReactFlow, {
    Background,
    Controls,
    Handle,
    Position,
    useEdgesState,
    useNodesState
} from 'react-flow-renderer';
import Avatar from '@mui/material/Avatar'; // Material UI Avatar

// Manager Node with Avatar and Click Handler
const ManagerNode = ({ data }) => (
    <div
        style={{
            padding: 10,
            border: '3px solid #rgb(24, 20, 54)',
            color: 'white',
            fontSize: 16,
            letterSpacing: 0.6,
            background: 'rgb(24, 20, 54)',
            borderRadius: 8,
            height: 60,
            fontWeight: 'bold',
            textAlign: 'center',
            minWidth: 250,
            position: 'relative',
            cursor: 'pointer'
        }}
        onClick={() => data.onClick?.()}
    >
        <Avatar style={{ margin: '0 auto', marginBottom: 4, fontSize: 16, width: 30, height: 30 }}>{data.label.charAt(0)}</Avatar>
        {data.label}
        <Handle type="source" position={Position.Bottom} />
        <Handle type="target" position={Position.Top} />
    </div>
);

const DepartmentNode = ({ data }) => (
    <div style={{
        padding: 10,
        border: '2px solid rgb(154, 162, 122)',
        background: '#E3EEB2',
        borderRadius: 6,
        textAlign: 'center',
        height: 30,
        minWidth: 200,
        position: 'relative',
    }}>
        <Handle type="target" position={Position.Top} />
        {data.label}
        <Handle type="source" position={Position.Bottom} />
    </div>
);

const EmployeeNode = ({ data }) => (
    <div style={{
        padding: 8,
        border: '1px solid #0099cc',
        background: '#f0faff',
        borderRadius: 4,
        textAlign: 'center',
        minWidth: 170,
        height: 'auto',
        position: 'relative',
    }}>
        <Avatar style={{ margin: '0 auto', marginBottom: 4, fontSize: 16, width: 30, height: 30 }}>{data.label.charAt(0)}</Avatar>
        <Handle type="target" position={Position.Top} />
        {data.label}
    </div>
);

const nodeTypes = {
    manager: ManagerNode,
    department: DepartmentNode,
    employee: EmployeeNode,
};

export default function Squads() {
    // Static data used for managers and their departments/employees
    const baseData = [
        {
            id: 'm1',
            name: 'Manager 1',
            type: 'manager',
            departments: [
                {
                    id: 'm1-d1',
                    name: 'Dept 1',
                    employees: [
                        { id: 'm1-d1-e1', name: 'Emp 1' },
                        { id: 'm1-d1-e2', name: 'Emp 2' },
                        { id: 'm1-d1-e3', name: 'Emp 3' },
                        { id: 'm1-d1-e4', name: 'Emp 4' },
                    ],
                },
                {
                    id: 'm1-d2',
                    name: 'Dept 2',
                    employees: [
                        { id: 'm1-d2-e1', name: 'Emp 1' },
                        { id: 'm1-d2-e2', name: 'Emp 2' },
                        { id: 'm1-d2-e3', name: 'Emp 3' },
                        { id: 'm1-d2-e4', name: 'Emp 4' },
                    ],
                },
                {
                    id: 'm1-d3',
                    name: 'Dept 3',
                    employees: [
                        { id: 'm1-d3-e1', name: 'Emp 1' },
                        { id: 'm1-d3-e2', name: 'Emp 2' },
                        { id: 'm1-d3-e3', name: 'Emp 3' },
                        { id: 'm1-d3-e4', name: 'Emp 4' },
                    ],
                },
            ],
        },
        { id: 'm2', name: 'Manager 2', type: 'manager', departments: [] },
        { id: 'm3', name: 'Manager 3', type: 'manager', departments: [] },
    ];

    const generalManagerId = 'general-manager';
    const [expandedManagers, setExpandedManagers] = useState([]);
    const [flowNodes, setNodes, onNodesChange] = useNodesState([]);
    const [flowEdges, setEdges, onEdgesChange] = useEdgesState([]);

    // Toggle department/employee visibility for a manager
    const toggleManager = (managerId) => {
        setExpandedManagers(prev => {
            const newExpanded = prev.includes(managerId)
                ? prev.filter(id => id !== managerId)
                : [...prev, managerId];
            return newExpanded;
        });
    };

    // Rebuild node and edge structure on state change
    useEffect(() => {
        const nodes = [];
        const edges = [];

        // Calculate horizontal center for the general manager
        const totalWidth = baseData.length * 400;
        const centerX = totalWidth / 1.5; // moving General manager in center

        nodes.push({
            id: generalManagerId,
            type: 'manager',
            data: { label: 'General Manager' },
            position: { x: centerX, y: 50 }, // Move generalmanager down
        });

        let currentX = 400; // Move manager from left to right
        baseData.forEach((manager) => {
            const managerNode = {
                id: manager.id,
                type: 'manager',
                data: {
                    label: manager.name,
                    onClick: () => toggleManager(manager.id),
                },
                position: { x: currentX, y: 300 }, // Move manager down
            };
            nodes.push(managerNode);

            // Purple edge from general manager to each manager
            edges.push({
                id: `e-${generalManagerId}-${manager.id}`,
                source: generalManagerId,
                target: manager.id,
                style: { stroke: 'green', strokeWidth: 2 },
            });

            if (expandedManagers.includes(manager.id)) {
                manager.departments.forEach((dept, dIndex) => {
                    const deptId = dept.id;
                    const deptX = managerNode.position.x;
                    const deptY = 500 + dIndex * 300;

                    nodes.push({
                        id: deptId,
                        type: 'department',
                        data: { label: dept.name },
                        position: { x: deptX, y: deptY },
                    });

                    // Orange edge from manager to department
                    edges.push({
                        id: `e-${manager.id}-${deptId}`,
                        source: manager.id,
                        target: deptId,
                        style: { stroke: '#ff9900', strokeWidth: 2 },
                    });

                    const baseEmpY = deptY + 150;
                    const spread = (dept.employees.length - 1) / 2;
                    dept.employees.forEach((emp, eIndex) => {
                        const empId = emp.id;
                        const empX = deptX + (eIndex - spread) * 250;

                        nodes.push({
                            id: empId,
                            type: 'employee',
                            data: { label: emp.name },
                            position: { x: empX, y: baseEmpY },
                        });

                        // Blue edge from department to employee
                        edges.push({
                            id: `e-${deptId}-${empId}`,
                            source: deptId,
                            target: empId,
                            style: { stroke: '#0099cc', strokeWidth: 1.5 },
                        });
                    });
                });
            }

            currentX += 400;
        });

        setNodes(nodes);
        setEdges(edges);
    }, [expandedManagers]);

    return (
        <div style={{ width: '100%', height: '100vh' }}>
            <ReactFlow
                nodes={flowNodes}
                edges={flowEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                defaultViewport={{ zoom: 0.4, x: 0, y: 0 }} // Zoomed out by default
                nodesDraggable={true}
                zoomOnScroll={true}
                panOnDrag={true}
            >
                <Controls showInteractive={false} />
                <Background />
            </ReactFlow>
        </div>
    );
}
